import { useQueryClient } from '@tanstack/react-query';
import { useCanWriteWorkspaceAsset } from '@vizit/access';
import type { BulkDeleteCollectionImagesFailure, ImageView } from '@vizit/api';
import { COLLECTION_ZIP_MAX_IMAGES, useAssetFavorites, useBulkDeleteCollectionImagesMutation } from '@vizit/api';
import { useBulkSetAssetFavoriteMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { ACTIVE_PDP_DELETE_REASONS, ACTIVE_PDP_ERROR_CODES } from '@vizit/constants';
import { CheckSquareFilledIcon, CompareIcon, DownloadMinimalIcon, HeartFilledIcon } from '@vizit/icons';
import { CompassIcon, HeartIcon, Trash01Icon } from '@vizit/icons';
import { type BulkAction, BulkActionBar } from '@vizit/molecules';
import { DeleteImagesDialog } from '@vizit/organisms';
import * as React from 'react';
import { invalidateDeletedCollectionImages } from './collection-asset-grid.remove-asset.hook';

const COMPARE_MAX_IMAGES = 10;

/**
 * Explains a bulk delete that left some images, e.g. "2 images, or Spark images made from them, are used as hero or
 * carousel images on product pages." The API also refuses an image when a Spark variant made from it is the one in use.
 */
function describeFailedDeletes(failed: BulkDeleteCollectionImagesFailure[]) {
  const inActivePdp = failed.filter((failure) => failure.error_code === ACTIVE_PDP_ERROR_CODES.image).length;
  const other = failed.length - inActivePdp;
  const reasons = [
    inActivePdp > 0 && `${ACTIVE_PDP_DELETE_REASONS.images(inActivePdp)}.`,
    other > 0 && `${other} ${other === 1 ? 'was' : 'were'} not found in this collection.`,
  ].filter(Boolean);
  return `${reasons.join(' ')} ${failed.length === 1 ? 'It stays' : 'They stay'} selected.`;
}

interface CollectionBulkActionBarProps {
  /**
   * Selected assets, in grid order.
   *
   * @required
   */
  selectedAssets: ImageView[];
  /**
   * Whether every asset on screen is selected already, which leaves "Select visible" nothing to do.
   *
   * @required
   */
  isOnScreenSelected: boolean;
  /**
   * Route workspace, used for assets whose payload has no `workspace_id`.
   *
   * @required
   */
  workspaceId: string;
  /**
   * Collection the assets are deleted from.
   *
   * @required
   */
  collectionId: string;
  /**
   * Selects every asset shown now.
   *
   * @required
   */
  onSelectVisible: () => void;
  /**
   * Clears the selection (× button and Esc).
   *
   * @required
   */
  onClear: () => void;
  /**
   * Whether favorites are on for this user. Without it there is no Favorite action.
   *
   * @optional
   * @default true
   */
  showFavorite?: boolean;
  /**
   * Downloads the selected images as one zip; the bar clears the selection right after (SDD D77).
   *
   * @required
   */
  onDownload: (imageIds: string[]) => void;
  onCompare?: () => void;
  onExploreTrends?: () => void;
  /**
   * Called when an action is done with the selection and the bar goes away: every selected image was deleted, or the
   * selection went to download. E.g. to move focus to the grid, since the bar that had it is gone.
   *
   * @optional
   */
  onDone?: () => void;
}

/**
 * Action bar for the Collection grid selection: "Select visible", Favorite (BLU-348), Download (BLU-351) and Delete
 * (BLU-353). Compare stays hidden until Comparison is released (SDD D42).
 */
function CollectionBulkActionBar(props: CollectionBulkActionBarProps) {
  const {
    selectedAssets,
    isOnScreenSelected,
    workspaceId,
    collectionId,
    onSelectVisible,
    onClear,
    showFavorite = true,
    onDownload,
    onDone,
    onCompare,
    onExploreTrends,
  } = props;
  const { isFavorite } = useAssetFavorites();
  const canWrite = useCanWriteWorkspaceAsset();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: deleteImages } = useBulkDeleteCollectionImagesMutation();
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
  const { mutate: setFavorites } = useBulkSetAssetFavoriteMutation({
    onError: () => {
      addToast({
        title: 'Could not update favorites',
        description: 'Nothing was changed. Please try again.',
        level: AlertLevel.Error,
      });
    },
  });

  // A write is addressed at each asset's own workspace (older payloads may omit it, then the route's)
  const workspaceOf = React.useCallback((asset: ImageView) => asset.workspace_id ?? workspaceId, [workspaceId]);

  // When everything selected is already a favorite, the action removes them instead (SDD D41)
  const isAllFavorite = selectedAssets.length > 0 && selectedAssets.every((asset) => isFavorite(asset));
  const canWriteAll = selectedAssets.every((asset) => canWrite({ workspace_id: workspaceOf(asset) }));
  // The bulk endpoint is all or nothing within one workspace. Splitting a mixed selection into one request per
  // workspace could leave it half changed, so such a selection can't be favorited at once
  const selectedWorkspaceIds = new Set(selectedAssets.map(workspaceOf));
  const [selectedWorkspaceId] = selectedWorkspaceIds;
  // An empty id (no `workspace_id` on the asset and none from the route) counts as no workspace, so the button is
  // disabled instead of doing nothing on press
  const isSingleWorkspace = selectedWorkspaceIds.size === 1 && Boolean(selectedWorkspaceId);

  const handleFavorite = React.useCallback(() => {
    if (!isSingleWorkspace || !selectedWorkspaceId) return;
    setFavorites({
      workspace_id: selectedWorkspaceId,
      image_ids: selectedAssets.map((asset) => asset.id),
      is_favorite: !isAllFavorite,
    });
  }, [isSingleWorkspace, selectedWorkspaceId, selectedAssets, setFavorites, isAllFavorite]);

  const handleDelete = React.useCallback(async () => {
    const imageIds = selectedAssets.map((asset) => asset.id);
    try {
      const { deleted_ids, failed } = await deleteImages({
        workspace_id: workspaceId,
        collection_id: collectionId,
        image_ids: imageIds,
      });
      setIsDeleteDialogOpen(false);
      if (deleted_ids.length > 0) {
        await invalidateDeletedCollectionImages(queryClient, { workspaceId, collectionId });
      }
      if (failed.length === 0) {
        onClear();
        onDone?.();
        return;
      }
      // Deleted images drop out of the selection once the grid refetches, the failed ones stay selected to try again
      addToast({
        title: `${failed.length} of ${imageIds.length} ${imageIds.length === 1 ? 'image was' : 'images were'} not deleted`,
        description: describeFailedDeletes(failed),
        level: AlertLevel.Error,
      });
    } catch {
      // Collection out of scope or the request failed: the server deleted nothing, so the selection stays as it was
      setIsDeleteDialogOpen(false);
      addToast({
        title: 'Could not delete images',
        description: 'Nothing was deleted. Please try again.',
        level: AlertLevel.Error,
      });
    }
  }, [selectedAssets, deleteImages, workspaceId, collectionId, queryClient, onClear, onDone, addToast]);

  // The download runs on with its own toasts; the selection is done with, as in the handoff (SDD D77)
  const handleDownload = React.useCallback(() => {
    onDownload(selectedAssets.map((asset) => asset.id));
    onClear();
    onDone?.();
  }, [onDownload, selectedAssets, onClear, onDone]);

  const actions: BulkAction[] = [
    {
      key: 'select-visible',
      label: 'Select visible',
      icon: <CheckSquareFilledIcon size={15} />,
      onPress: onSelectVisible,
      isDisabled: isOnScreenSelected,
      title: 'Select the images on screen',
      disabledReason: 'All images on screen are selected. Scroll to see more',
    },
    ...(onCompare
      ? [
          {
            key: 'compare',
            label: 'Compare',
            icon: <CompareIcon size={15} />,
            onPress: onCompare,
            isDisabled: selectedAssets.length < 2 || selectedAssets.length > COMPARE_MAX_IMAGES,
            title: `Compare ${selectedAssets.length} images`,
            disabledReason:
              selectedAssets.length < 2
                ? 'Select 2 or more images to compare'
                : `Select up to ${COMPARE_MAX_IMAGES} images to compare`,
          },
        ]
      : []),
    ...(onExploreTrends
      ? [
          {
            key: 'explore-trends',
            label: 'Explore visual trends',
            icon: <CompassIcon size={15} />,
            onPress: onExploreTrends,
            isDisabled: selectedAssets.length !== 1,
            tone: 'primary' as const,
            title: 'See how this image ranks among the images of a custom benchmark',
            disabledReason: 'Select only one image to explore visual trends',
          },
        ]
      : []),
    ...(showFavorite
      ? [
          {
            key: 'favorite',
            label: isAllFavorite ? 'Unfavorite' : 'Favorite',
            icon: isAllFavorite ? <HeartFilledIcon size={15} /> : <HeartIcon size={15} />,
            onPress: handleFavorite,
            isDisabled: !canWriteAll || !isSingleWorkspace,
            disabledReason: isSingleWorkspace
              ? "You don't have access to change favorites for some of these images"
              : 'Select images from one workspace to change favorites',
          },
        ]
      : []),
    {
      key: 'download',
      label: 'Download',
      icon: <DownloadMinimalIcon size={15} />,
      onPress: handleDownload,
      // One zip holds up to 200 images, the API refuses more (SDD D49)
      isDisabled: selectedAssets.length > COLLECTION_ZIP_MAX_IMAGES,
      title: 'Download selected images as a .zip',
      disabledReason: `Select up to ${COLLECTION_ZIP_MAX_IMAGES} images to download at once`,
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <Trash01Icon size={15} />,
      onPress: () => setIsDeleteDialogOpen(true),
      tone: 'danger',
      isDisabled: !canWriteAll,
      title: 'Delete selected images',
      disabledReason: "You don't have access to delete some of these images",
    },
  ];

  return (
    <>
      <BulkActionBar count={selectedAssets.length} actions={actions} onClear={onClear} />
      <DeleteImagesDialog
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        count={selectedAssets.length}
        onConfirm={handleDelete}
      />
    </>
  );
}

export { COMPARE_MAX_IMAGES, CollectionBulkActionBar };
export type { CollectionBulkActionBarProps };
